import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { themeSSRManager } from '@confluence/ssr-utilities/entry-points/ThemeSSRManager';
import { getEffectiveThemeState } from '@confluence/theming-utils/entry-points/getEffectiveTheme';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type {
	UserSettingsQuery as UserSettingsQueryType,
	UserSettingsQueryVariables as UserSettingsQueryVariablesType,
} from './__types__/UserSettingsQuery';
import { UserSettingsQuery } from './UserSettingsQuery.graphql';
import { preloadWorkspaceThemeForSSR } from './preloadWorkspaceThemeForSSR';

export const API_DEFAULT_THEME_STATE = 'default';

export const preloadUserSettingsForSSR = async ({ themeState }: { themeState?: string }) => {
	const { isLicensed, isLoggedIn } = getPreloaderFnContext();

	if (!isLoggedIn) {
		return;
	}

	// Apply themeState immediately if provided via query param (always defined for embedded pages)
	if (themeState) {
		themeSSRManager.setThemeStateBasedOnSavedThemeState(
			getEffectiveThemeState(decodeURIComponent(themeState)),
		);
	}

	// Always fetch user settings
	// When shared-user-preferences flag is on, also fetch the workspace theme from AGG in parallel
	const userSettingsPromise = query<UserSettingsQueryType, UserSettingsQueryVariablesType>({
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		query: UserSettingsQuery,
		variables: {
			isNotLicensed: !isLicensed,
		},
	});

	const sharedThemePromise = fg('shared-user-preferences')
		? preloadWorkspaceThemeForSSR()
		: Promise.resolve(null);

	await waitForAllReturnWhenError([userSettingsPromise, sharedThemePromise]);

	const userSettingsResponse = await userSettingsPromise;
	const workspaceTheme = await sharedThemePromise;

	const response = userSettingsResponse;
	const legacyTheme = response?.data?.userPreferences?.theme;

	// When shared-user-preferences flag is enabled, prefer the workspace theme over the legacy theme
	const theme = workspaceTheme && fg('shared-user-preferences') ? workspaceTheme : legacyTheme;

	// Apply user preference theme only when no themeState is provided (prevents flicker from mismatch)
	if (expValEquals('confluence_preview_panels_exp', 'isEnabled', true)) {
		if (!themeState && theme) {
			themeSSRManager.setThemeStateBasedOnSavedThemeState(getEffectiveThemeState(theme));
		}
	} else {
		if (theme) {
			themeSSRManager.setThemeStateBasedOnSavedThemeState(getEffectiveThemeState(theme));
		}
	}

	return response;
};
